//#region types
import { MonthInputProps, NumberInputProps } from "models/propTypes/wintFormPropsTypes";
import { WithdrawalSectionProps } from "models/propTypes/wintSalaryPropTypes";
import { SimpleTableRow } from "models/propTypes/simpleTableProps";
import { StyleEnums } from "models/enums";
import { WintSuperkollAPIModelWintSalaryDeviations } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import React from "react";
import { FormFieldLayout, NumberInput, VerticalFormField } from "components/common/wintForms";
import MonthInput from "components/common/wintForms/formFields/monthInput";
import { SimpleAccordionTable, StaticInfoMessage } from "components/common";
//#endregion
import { CurrencyHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

/**
 * Saved-salary / vacation-pay withdrawal input section, also used for the saved-salary deposit.
 *
 * Shows the pot balances the selected classification draws from, then a single positive amount
 * on a month locked to the current one. CurrentMonthPlacement.Always means the deviation lands on
 * the month it occurs in rather than in arrears — it is the lock, not the server, that keeps a
 * withdrawal on the current month. A deposit has no classification and draws on no pot, so it shows
 * no balances.
 *
 * A negative balance renders negative: it is legitimate (absence over cash pay, a commission
 * clawback) and is exactly what the person filing needs to see. Never floored to zero.
 */
const WithdrawalSection: React.FC<WithdrawalSectionProps> = (props) => {
  const { connect, classification, balances, balancesLoading, balancesFailed, cap, editMode, readOnly } = props;

  const Classification = WintSuperkollAPIModelWintSalaryDeviations.SalaryDeviationTypeClassification;
  const isDeposit = classification === undefined;
  const showSavedSalary = classification !== Classification.SalaryVacationPayWithdrawal;
  const showVacationPay = classification !== Classification.SavedSalaryWithdrawal;

  // Every pot the classification draws on must be present: a combined withdrawal missing one of
  // them would otherwise render as a complete single-pot panel, with no cap and nothing saying so.
  const potMissing = (showSavedSalary && !balances?.savedSalary) || (showVacationPay && !balances?.vacationPay);

  const balanceRows: SimpleTableRow[] = [];
  if (showSavedSalary && balances?.savedSalary) {
    balanceRows.push({
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.WithdrawalSavedSalaryPot),
      value: CurrencyHelper.getFormattedCurrencyInSEK(balances.savedSalary.balance),
    });
  }
  if (showVacationPay && balances?.vacationPay) {
    balanceRows.push({
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.WithdrawalVacationPayPot),
      value: CurrencyHelper.getFormattedCurrencyInSEK(balances.vacationPay.balance),
    });
  }

  const renderBalances = () => {
    // Not-yet-requested reads as loading: this section paints on the frame the type is picked,
    // while the container only learns the classification from the passive onFormUpdate effect and
    // starts the read after that. Without this the warning flashes before a request exists.
    if (balancesLoading || (!balancesFailed && balances === undefined)) return undefined;

    if (balancesFailed || potMissing) {
      return (
        <StaticInfoMessage displayType={StyleEnums.DisplayType.Warning}>
          {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.WithdrawalBalanceUnavailable)}
        </StaticInfoMessage>
      );
    }

    return <SimpleAccordionTable variant="secondary" items={balanceRows} />;
  };

  return (
    <>
      <VerticalFormField<MonthInputProps>
        Component={MonthInput}
        label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.Month)}
        {...connect("withdrawal.yearMonth")}
        // Dropped in edit mode: the field is seeded from the persisted fromDate, so a withdrawal
        // filed in an earlier month still shows that month, and the caption would then promise a
        // payday it does not have.
        descriptionText={
          editMode
            ? undefined
            : LocalizationHelperInstance.getFormattedMessage(
                isDeposit
                  ? TranslationKeys.SalaryDeviation.DepositMonthDescription
                  : TranslationKeys.SalaryDeviation.WithdrawalMonthDescription
              )
        }
        showMainLabel
        // Always locked, not just when the form is read-only: the placement follows the submitted
        // dates, so this lock is the only thing keeping a new withdrawal on the current month.
        disabled
      />
      {!isDeposit && (
        <FormFieldLayout
          direction="vertical"
          label={LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.SalaryDeviation.WithdrawalAvailableBalance
          )}
          className={cssExports.withdrawalBalances}
        >
          {renderBalances()}
        </FormFieldLayout>
      )}
      <VerticalFormField<NumberInputProps>
        Component={NumberInput}
        {...connect("withdrawal.amount")}
        label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.WithdrawalAmount)}
        icon={IconDefinitionConstants.WMoney}
        suffix="kr"
        decimalPoints={2}
        allowNegative={false}
        descriptionText={
          cap !== undefined
            ? LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.SalaryDeviation.WithdrawalMaxDescription,
                "",
                { max: CurrencyHelper.getFormattedCurrencyInSEK(cap) }
              )
            : undefined
        }
        showMainLabel
        disabled={readOnly}
      />
    </>
  );
};

export default WithdrawalSection;
